import { createContext, FC, useEffect, useMemo, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { levelEnum } from '../../ts-config';
import { allData, businessStatus, rankOptions } from './ts-config';
import useUpdateEffect from '@/common/hook/updateEffect/useUpdateEffect';
import LocationEvent from '../LocationEvent';
import LoadMapData from '../LoadMapData';
import MapCon from './MapCon';
import RightCon from './RightCon';

export interface LcnSitemapSiteViewContextProps {
  /** 完整的筛选参数，将提供给场地、点位筛选 */
  fullSearchParams: {
    requirementId, // 需求id
    [p: string]: any,
   },
}

export const LcnSitemapSiteViewContext = createContext<LcnSitemapSiteViewContextProps>({
  fullSearchParams: {
    requirementId: null,
  },
});

/**
 * @Description  选址视角-入口文件
 */
const SiteView:FC<{
  cityProvinceInfo,
  viewLevel, // 视图level
  mapIns,
  setCollectList,
  locationInfo,
  poiData,
  level,
  curSelectDistrict,
  isShape,
  isReset,
  showLeftCon,
  setShowLeftCon,
  rightDrawerVisible,
  setRightDrawerVisible,
  isOpenBorder,
  itemData,
  setItemData,
  searchParams,
  districtCluster,
  setDistrictCluster,
  isSelectDistrictRef,
}> = ({
  cityProvinceInfo,
  viewLevel, // 视图level
  mapIns,
  setCollectList,
  locationInfo,
  poiData,
  level,
  curSelectDistrict,
  isShape,
  isReset,
  showLeftCon,
  setShowLeftCon,
  rightDrawerVisible,
  setRightDrawerVisible,
  isOpenBorder,
  itemData,
  setItemData,
  searchParams,
  districtCluster,
  setDistrictCluster,
  isSelectDistrictRef,
}) => {

  const { isPositioning } = LocationEvent(
    mapIns,
    locationInfo
  ) || {};// 定位相关

  const [rankSort, setRankSort] = useState<any>(rankOptions.find(item => item.value === allData) || rankOptions[0]); // 默认全部
  const [pointDataIds, setPointDataIds] = useState<any[]>([]); // 获取点位数据对应的id，供请求围栏接口使用，所以这里不需要过滤200名后的数据

  const [isBatchCollect, setIsBatchCollect] = useState<boolean>(false); // 是否是批量收藏状态收藏
  const [batchCollectIds, setBatchCollectIds] = useState<number[]>([]); // 批量收藏的开店区域
  const {
    polygonData,
    districtData,
    isLoading,
    /** 完整的筛选参数，将提供给场地、点位筛选 */
    fullSearchParams,
  } = LoadMapData({
    isPositioning,
    mapIns,
    level,
    searchParams,
    isReset,
    setItemData,
    isOpenBorder,
    viewLevel,
    pointDataIds
  });

  // 判断有无生成中、收藏数据
  const hasOtherData = useMemo(() => {
    const { createLength } = districtData?.reduce((acc, item) => {
      if (item?.isFavourate) {
        acc.favourateList.push(item.id);
      }
      if (item?.status === businessStatus.NEW) {
        acc.createLength += 1;
      }
      return acc;
    }, { favourateList: [], createLength: 0 });
    // 注释原因 Cannot update a component (`Siteselectionmapb`) while rendering a different component (`SiteView`) 最好不要在useMemo里使用setState(子组件里去set父组件的state)，改用useEffect
    // setCollectList(favourateList);
    return {
      hasCreating: createLength
    };
  }, [districtData]);

  const handlerZoomEnd = () => {
    // bigdataBtn('ee1220df-2b03-eb44-a93c-64b312390c39', '选址地图', '地图中-放大/缩小一次', '点击了地图中-放大/缩小一次');
  };
  const handlerDragEnd = () => {
    // bigdataBtn('9999a8ad-693e-0778-fac5-3e155c747f89', '选址地图', '地图中-拖动一次', '点击了地图中-拖动一次');
  };

  const methods = useMethods({
    handleQuitBatch: () => { // 退出批量收藏操作
      setIsBatchCollect(false); // 退出批量收藏的状态
      setBatchCollectIds([]); // 将批量收藏状态下的选中项清空
    }
  });

  // 判断当前zoom是否可显示商圈围栏
  useEffect(() => {
    if (!mapIns) return;
    // 监听地图zoom的变化，到指定的zoom时，显示围栏
    mapIns.on('zoomend', handlerZoomEnd);
    mapIns.on('dragend', handlerDragEnd);
    mapIns.setZooms([9, 20]);
    return () => {
      mapIns.off('zoomend', handlerZoomEnd);
      mapIns.off('dragend', handlerDragEnd);
    };
  }, [mapIns]);


  // 结束新增开店区域后，打开左侧右侧列表
  useUpdateEffect(() => {
    if (!isShape) {
      setRightDrawerVisible(true);
      setShowLeftCon(true);
    }
    // 新增开店区域时，退出批量收藏状态
    isShape && methods.handleQuitBatch();
  }, [isShape]);

  // 当到其他层级时，清空地图
  useEffect(() => {
    return () => {
      mapIns?.clearMap();
      methods.handleQuitBatch();
    };
  }, [mapIns]);
  // 切换城市时退出批量操作
  useEffect(() => {
    // 不能把isBatchCollect加入依赖项，只是在城市id变化时判断下是否在批量收藏状态下
    isBatchCollect && methods.handleQuitBatch();
  }, [viewLevel?.cityId]);

  useEffect(() => {
    // 退出批量收藏状态时
    if (!isBatchCollect) {
      methods.handleQuitBatch();
    }
  }, [isBatchCollect]);

  useEffect(() => {
    // prd:选择城市后点击确定，跳到对应城市的选址视角，定位到当前城市中心点15级即可。
    if (cityProvinceInfo?.level === levelEnum.site && cityProvinceInfo?.lng) {
      mapIns?.setZoomAndCenter(15, [+cityProvinceInfo?.lng, +cityProvinceInfo?.lat], false, 100);
    }
  }, [cityProvinceInfo?.level, cityProvinceInfo?.id, mapIns]);

  useEffect(() => {
    const { favourateList } = districtData?.reduce((acc, item) => {
      if (item?.isFavourate) {
        acc.favourateList.push(item.id);
      }
      return acc;
    }, { favourateList: [] });

    setCollectList(favourateList);
  }, [districtData]);


  return <div>
    <MapCon
      cityProvinceInfo={cityProvinceInfo}
      mapIns={mapIns}
      level={level}
      curSelectDistrict={curSelectDistrict}
      districtData={districtData}
      itemData={itemData}
      setItemData={setItemData}
      locationInfo={locationInfo}
      polygonData={polygonData}
      rankSort={rankSort}
      isShape={isShape}
      isSelectDistrictRef={isSelectDistrictRef}
      isOpenBorder={isOpenBorder}
      isBatchCollect={isBatchCollect}
      batchCollectIds={batchCollectIds}
      setBatchCollectIds={setBatchCollectIds}
      setPointDataIds={setPointDataIds}
    />
    <LcnSitemapSiteViewContext.Provider value={{ fullSearchParams }}>
      <RightCon
        mapIns={mapIns}
        level={level}
        locationInfo={locationInfo}
        searchParams={searchParams}
        itemData={itemData} // 选中的商圈项
        poiData={poiData} // 地址搜索
        listData={districtData} // 列表数据
        setItemData={setItemData}
        rankSort={rankSort}
        setRankSort={setRankSort}
        isLoading={isLoading} // 右侧是否加载中
        setOpenList={setRightDrawerVisible} // 设置右边展开
        openList={rightDrawerVisible} // 右侧是否展开
        districtCluster={districtCluster} // 点击区时
        setDistrictCluster={setDistrictCluster}
        hasOtherData={hasOtherData} // 是否存在生成中的数据
        isBatchCollect={isBatchCollect}
        setIsBatchCollect={setIsBatchCollect}
        batchCollectIds={batchCollectIds}
        setBatchCollectIds={setBatchCollectIds}
        handleQuitBatch={methods.handleQuitBatch}
        cityProvinceInfo={cityProvinceInfo}
        showLeftCon={showLeftCon}
      />
    </LcnSitemapSiteViewContext.Provider>
  </div>;
};
export default SiteView;
